import { useEffect, useRef, useState } from 'react'

import { t } from '../../../../common/i18n.js'
import { PlaytimeLeaderboardData, PlaytimeProfileData } from '../../../../common/types/playtime.js'
import { buildLeaderboardHoverCard } from '../../../../common/utils/leaderboard-hover-card.js'
import { LeaderboardHoverCard } from '../../../components/leaderboard-hover-card/leaderboard-hover-card.js'
import { LeaderboardTable } from '../../../components/leaderboard-table/leaderboard-table.js'
import { LevelProgress } from '../../../components/level-progress/level-progress.js'
import { PageHeader } from '../../../components/page-header/page-header.js'
import { PageShell } from '../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../components/spinner/spinner.js'
import { Tabs } from '../../../components/tabs/tabs.js'
import { ToggleGroup } from '../../../components/toggle-group/toggle-group.js'
import { DataState, View } from '../../../types.js'
import { ViewBaseProps } from '../../base/base.js'
import '../playtime-layout.css'
import { getHoverCardPlacement, getLevelProgressSubtitle } from '../playtime-utils.js'
import './leaderboard.scss'

type ScoreKey = 'totalScore' | 'scoreWithoutAnniversary'

const HOVER_DELAY_MS = 300
// Cache fetched profiles for the whole SPA session — hovering the same user
// twice must not refetch. Same session-lifetime caching as the leaderboard
// data itself.
const hoverProfileCache = new Map<number, PlaytimeProfileData>()

export interface LeaderboardViewProps {
    base: ViewBaseProps
    leaderboard: DataState<PlaytimeLeaderboardData>
    profile: DataState<PlaytimeProfileData>
    currentUserId: number
    view: View
}

export const LeaderboardView = (props: LeaderboardViewProps) => {
    const [scoreKey, setScoreKey] = useState<ScoreKey>(
        'scoreWithoutAnniversary',
    )
    const [hoverProfile, setHoverProfile] = useState<{
        userId: number
        profile: PlaytimeProfileData
    } | null>(null)
    const hoveredUserRef = useRef<number | null>(null)
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const { data, isLoading } = props.leaderboard
    const { data: profileData, isLoading: isLoadingProfile } = props.profile

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
        }
    }, [])

    if (isLoading || isLoadingProfile || !data || !profileData) {
        return <Spinner />
    }

    const lang = props.view.state.lang
    const lv = t.playtime.leaderboardView

    const handleHoverStart = (userId: number) => {
        if (window.matchMedia('(hover: none)').matches) return
        hoveredUserRef.current = userId
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
        hoverTimerRef.current = setTimeout(() => {
            void (async () => {
                try {
                    let profile = hoverProfileCache.get(userId)
                    if (!profile) {
                        const res = await props.view.api.playtime.getUserProfile(userId)
                        profile = res.data
                        hoverProfileCache.set(userId, profile)
                    }
                    // The mouse may have left (or moved to another row) during the fetch
                    if (hoveredUserRef.current === userId) {
                        setHoverProfile({ userId, profile })
                    }
                } catch {
                    // Preview is best-effort — a failed fetch just means no card
                }
            })()
        }, HOVER_DELAY_MS)
    }

    const handleHoverEnd = () => {
        hoveredUserRef.current = null
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
        setHoverProfile(null)
    }

    const sortedUsers = [...data.users].sort((a, b) => b[scoreKey] - a[scoreKey])
    // Single lookup, shared with the row itself, so the card's name/avatar
    // always agree with what's rendered in the table.
    const hoveredIndex = hoverProfile
        ? sortedUsers.findIndex((u) => u.id === hoverProfile.userId)
        : -1
    const hoveredUser = hoveredIndex >= 0 ? sortedUsers[hoveredIndex] : undefined
    const hoverCardPlacement = getHoverCardPlacement(hoveredIndex, sortedUsers.length)

    const activeRank = scoreKey === 'totalScore' ? data.currentUserRankTotal : data.currentUserRank
    const subtitle = getLevelProgressSubtitle(
        profileData.totalScore,
        profileData.level,
        profileData.nextThreshold,
        activeRank,
        lang,
    )

    const tabItems = [
        { key: 'badges', label: t.playtime.levelProgress.badges(lang), href: '/playtime' },
        { key: 'leaderboard', label: t.playtime.leaderboard(lang), href: '/playtime/leaderboard' },
    ]

    const toolbar = (
        <ToggleGroup<ScoreKey>
            options={[
                { value: 'scoreWithoutAnniversary', label: lv.withoutAnniversary(lang) },
                { value: 'totalScore', label: lv.withAnniversary(lang) },
            ]}
            value={scoreKey}
            onChange={setScoreKey}
            ariaLabel={lv.toggleLabel(lang)}
        />
    )

    return (
        <PageShell className="playtime-page">
            <PageHeader title={t.playtime.profile(lang)} />

            <LevelProgress
                score={profileData.totalScore}
                level={profileData.level}
                nextThreshold={profileData.nextThreshold}
                claimedBadgesCount={profileData.claimedBadgesCount}
                totalBadgesCount={profileData.badges.length}
                lang={lang}
                subtitle={subtitle}
            />

            <Tabs
                variant="underline"
                items={tabItems}
                activeKey="leaderboard"
                actions={toolbar}
                lang={lang}
            />

            <div className="playtime__panel">
                <div className="leaderboard">
                    <LeaderboardTable
                        users={sortedUsers}
                        currentUserId={props.currentUserId}
                        scoreKey={scoreKey}
                        lang={lang}
                        userLink={{
                            toHref: (id) => `/badges/user/${id}`,
                            onHoverStart: handleHoverStart,
                            onHoverEnd: handleHoverEnd,
                            hoverCard: hoverProfile
                                ? {
                                      userId: hoverProfile.userId,
                                      placement: hoverCardPlacement,
                                      content: (
                                          <LeaderboardHoverCard
                                              name={hoveredUser?.name ?? null}
                                              avatar={hoveredUser?.avatar ?? null}
                                              lang={lang}
                                              data={buildLeaderboardHoverCard(
                                                  hoverProfile.profile,
                                                  lang,
                                                  scoreKey,
                                              )}
                                          />
                                      ),
                                  }
                                : null,
                        }}
                    />
                </div>
            </div>
        </PageShell>
    )
}
